import useScrollReveal from '../../hooks/useScrollReveal';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from '../../components/feature/HeroSection';
import TrustBar from '../../components/feature/TrustBar';
import AIAnswerBlock from '../../components/feature/AIAnswerBlock';
import MidCTABanner from '../../components/feature/MidCTABanner';
import ProcessSection from '../../components/feature/ProcessSection';
import WhyChooseUs from '../../components/feature/WhyChooseUs';
import TestimonialsSection from '../../components/feature/TestimonialsSection';
import FAQSection from '../../components/feature/FAQSection';
import FinalCTA from '../../components/feature/FinalCTA';

const locations = [
  {
    city: "St. Mary's",
    state: 'GA',
    primary: true,
    services: ['Wood Fence', 'Vinyl Fence', 'Chain-Link', 'Aluminum', 'Steel', 'Custom Gates'],
    desc: "Our home base — Masters Fencing has proudly served St. Mary's for 20+ years. Top-rated fencing contractor for residential and commercial properties throughout the area.",
  },
  {
    city: "King's Bay",
    state: 'GA',
    primary: false,
    services: ['Chain-Link', 'Wood Fence', 'Vinyl Fence', 'Steel Fence'],
    desc: "Serving the King's Bay community with quality fencing — chain-link security, cedar privacy fences, and vinyl installations for homeowners near the naval base.",
  },
  {
    city: 'Highland Oaks',
    state: 'GA',
    primary: false,
    services: ['Aluminum Fence', 'Vinyl Fence', 'Wood Fence'],
    desc: 'Premium fencing for Highland Oaks properties. Custom wood, vinyl, and aluminum fences tailored to this established Camden County neighborhood.',
  },
  {
    city: 'Sugar Mill Plantation',
    state: 'GA',
    primary: false,
    services: ['Aluminum Fence', 'Vinyl Fence', 'Custom Gates'],
    desc: 'Elegant fencing solutions for Sugar Mill Plantation homes. Aluminum and vinyl installations complement the refined aesthetic of this sought-after community.',
  },
  {
    city: 'Kingsland',
    state: 'GA',
    primary: false,
    services: ['Chain-Link', 'Wood Fence', 'Steel Fence', 'Vinyl Fence'],
    desc: 'Trusted fencing contractor serving Kingsland, GA. Chain-link, wood, steel, and vinyl fence installations at competitive pricing with expert local service.',
  },
  {
    city: 'River Cove',
    state: 'GA',
    primary: false,
    services: ['Wood Fence', 'Vinyl Fence', 'Aluminum Fence'],
    desc: 'Quality fencing for River Cove properties. Whether for privacy, security, or curb appeal — we deliver reliable installations throughout this Camden County community.',
  },
  {
    city: 'Woodbine',
    state: 'GA',
    primary: false,
    services: ['Wood Fence', 'Chain-Link', 'Steel Fence'],
    desc: "Masters Fencing serves Woodbine with durable fencing for homes and businesses. As Camden County's county seat, neighbors trust us for quality, lasting fence work.",
  },
  {
    city: 'White Oak',
    state: 'GA',
    primary: false,
    services: ['Chain-Link', 'Wood Fence', 'Steel Fence'],
    desc: 'Expert fence installation in White Oak, Georgia — residential and rural properties served with chain-link, wood, and steel built to withstand the coastal elements.',
  },
  {
    city: 'Homeland',
    state: 'GA',
    primary: false,
    services: ['Chain-Link', 'Wood Fence', 'Vinyl Fence'],
    desc: 'Serving Homeland, GA with honest pricing and professional fence installation. Quality fencing for rural and residential properties throughout southern Camden County.',
  },
];

const LocationsPage: React.FC = () => {
  useScrollReveal();

  return (
    <div className="bg-[#FAF8F5]">
      <Navbar />
      <main>
        <HeroSection
          label="Service Areas · Camden County, Georgia"
          headline="Fencing St. Mary's & Surrounding Communities"
          subheadline="Locally rooted for 20+ years — Masters Fencing serves homeowners and businesses throughout Camden County and Southeast Georgia. We know these communities because we live and build here every day."
          bgImage="https://public.readdy.ai/ai/img_res/d034bd1df959f7a178dd5420debaf03d.jpg"
          primaryCTA="Request a Consultation"
        />
        <TrustBar />

        {/* Map + Coverage overview */}
        <section className="relative bg-[#FAF8F5]">
          <div className="section-py max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start mb-14">

              {/* Left */}
              <div className="reveal-left">
                <div className="flex items-center gap-3 mb-6">
                  <span className="gold-bar" />
                  <span className="text-[#C4A84F] text-[11px] tracking-[0.38em] uppercase font-sans font-medium">
                    Our Coverage Area
                  </span>
                </div>
                <h2 className="font-serif text-[3.2rem] md:text-[4.2rem] text-[#0D0C0B] leading-[0.95] mb-6">
                  Serving All of Camden<br />County &amp; Beyond
                </h2>
                <p className="text-[#0D0C0B]/52 text-base leading-relaxed font-sans mb-8">
                  Based in St. Mary's, Masters Fencing provides expert fencing installation across Camden County and the surrounding Southeast Georgia region. Our deep local expertise means we understand the terrain, coastal climate, and community needs of every area we serve.
                </p>
                <a
                  href="tel:912-409-2031"
                  className="inline-flex items-center gap-3 bg-[#C4A84F] text-[#0D0C0B] text-[11px] font-bold tracking-[0.2em] uppercase px-8 py-4 hover:bg-[#D4BC78] transition-all duration-300 whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-[10px]" />
                  Confirm Your Area — 912-409-2031
                </a>
              </div>

              {/* Right — map */}
              <div className="reveal-right">
                <div className="overflow-hidden border border-[#0D0C0B]/8">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d111408.14!2d-81.6164476!3d30.863178!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e503b1bb249029%3A0x9bb706a29a9493cf!2sMasters%20Fencing!5e0!3m2!1sen!2sus!4v1680000000000!5m2!1sen!2sus"
                    width="100%" height="460"
                    style={{ border: 0, display: 'block' }}
                    allowFullScreen loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Masters Fencing service area map — Camden County Georgia"
                  />
                </div>
                <div className="bg-[#0D0C0B] px-6 py-5 flex items-center gap-4">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#C4A84F] shrink-0">
                    <i className="fa-solid fa-location-dot text-[#0D0C0B] text-sm" />
                  </div>
                  <div>
                    <div className="text-[#F5F1E8] text-sm font-semibold font-sans">
                      104 Admiral's Walk Dr, St. Marys, GA 31558
                    </div>
                    <div className="text-[#F5F1E8]/38 text-xs font-sans mt-0.5">
                      Mon–Sat: 6:00 am–6:00 pm · Sunday: Closed
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Location cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
              {locations.map((loc) => (
                <div
                  key={loc.city}
                  className={`reveal p-7 border transition-all duration-300 hover:-translate-y-1 ${
                    loc.primary
                      ? 'bg-[#0D0C0B] border-[#C4A84F]'
                      : 'bg-white border-[#0D0C0B]/8 hover:border-[#C4A84F]/45'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <i className="fa-solid fa-location-dot text-[#C4A84F] text-sm" />
                    <h3 className={`font-serif text-[1.3rem] leading-tight ${loc.primary ? 'text-[#F5F1E8]' : 'text-[#0D0C0B]'}`}>
                      {loc.city}, {loc.state}
                    </h3>
                    {loc.primary && (
                      <span className="ml-auto text-[8px] tracking-wider uppercase font-sans text-[#C4A84F] border border-[#C4A84F]/50 px-2 py-0.5 shrink-0">
                        HQ
                      </span>
                    )}
                  </div>
                  <p className={`text-sm leading-relaxed font-sans mb-5 ${loc.primary ? 'text-[#F5F1E8]/50' : 'text-[#0D0C0B]/52'}`}>
                    {loc.desc}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {loc.services.map((sv) => (
                      <span
                        key={sv}
                        className={`text-[9px] tracking-wider uppercase font-sans px-2 py-1 border ${
                          loc.primary
                            ? 'border-[#C4A84F]/28 text-[#C4A84F]/65'
                            : 'border-[#0D0C0B]/10 text-[#0D0C0B]/38'
                        }`}
                      >
                        {sv}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <MidCTABanner
          headline="Your Neighborhood, Our Expertise"
          sub="Serving all of Camden County for 20+ years. Free estimates, $100 off same-day signing."
          cta="Request a Free Estimate"
        />
        <AIAnswerBlock
          focusTitle="Local Fencing Expertise Across Camden County"
          focusDescription="Masters Fencing has spent over 20 years building fences and building trust across St. Mary's, Georgia and the broader Camden County region. Our deep knowledge of local coastal conditions, soil types, and community needs means every fence we install is designed to perform and look exceptional for the long haul."
        />
        <ProcessSection />
        <WhyChooseUs />
        <TestimonialsSection />
        <FAQSection />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default LocationsPage;
